import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Observable } from 'rxjs';

import {
  InsuranceState,
  InsuranceStatusPayload,
  InsuranceTrigger,
} from '../../../../../core/df/df.types';
import { ModalComponent } from '../../../../../shared/modal/modal';
import { fieldError, flagMissing, placeRefusal } from '../../../../../shared/modal/modal-refusal';
import { GENERIC_ERROR } from '../../../../../core/http/read-error';

export interface InsuranceStatusDialogData {
  /** The business's status now; null when none has been recorded. */
  readonly current: InsuranceState | null;
  readonly triggers: Record<'SUSPENDED' | 'WITHDRAWN', InsuranceTrigger[]>;
  readonly save: (payload: InsuranceStatusPayload) => Observable<unknown>;
}

interface Move {
  readonly to: InsuranceState;
  readonly label: string;
}

/** The moves `§C7` allows from each state; the server is the authority. */
function movesFrom(current: InsuranceState | null): Move[] {
  if (current === null) return [{ to: 'ACTIVE', label: 'Record as active' }];
  if (current === 'ACTIVE') {
    return [
      { to: 'SUSPENDED', label: 'Suspend' },
      { to: 'WITHDRAWN', label: 'Withdraw (permanent)' },
    ];
  }
  if (current === 'SUSPENDED') {
    return [
      { to: 'ACTIVE', label: 'Reinstate' },
      { to: 'WITHDRAWN', label: 'Withdraw (permanent)' },
    ];
  }
  return [];
}

/**
 * `§C7.3` / `§C8` — set the business's insurance status. Suspending names its trigger;
 * reinstating needs the trigger resolved, a Credit Committee reference and no new
 * adverse events, together; withdrawing needs a reference and is permanent. A
 * transition is a new audit row, never an edit (N8). Closes only once recorded.
 */
@Component({
  selector: 'app-insurance-status-dialog',
  imports: [
    ModalComponent,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  template: `
    <app-modal
      heading="Insurance status"
      description="Set by a person: there is no insurer feed. Every change stays on record with your name."
      submitLabel="Record"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      <div class="modal-fields">
        <mat-form-field appearance="outline">
          <mat-label>Change</mat-label>
          <mat-select formControlName="to_state" data-testid="ins-to-state">
            @for (m of moves; track m.to) {
              <mat-option [value]="m.to">{{ m.label }}</mat-option>
            }
          </mat-select>
          <mat-error>{{ fieldError(form.controls.to_state) ?? 'Choose the change.' }}</mat-error>
        </mat-form-field>

        @if (triggerOptions().length > 0) {
          <mat-form-field appearance="outline">
            <mat-label>Trigger</mat-label>
            <mat-select formControlName="trigger_id" data-testid="ins-trigger">
              @for (t of triggerOptions(); track t.value) {
                <mat-option [value]="t.value">{{ t.label }}</mat-option>
              }
            </mat-select>
            <mat-error>{{ fieldError(form.controls.trigger_id) ?? 'Name the trigger.' }}</mat-error>
          </mat-form-field>
        }

        @if (needsReference()) {
          <mat-form-field appearance="outline">
            <mat-label>Credit Committee reference</mat-label>
            <input matInput formControlName="cc_ref" data-testid="ins-cc-ref" />
            <mat-error>{{
              fieldError(form.controls.cc_ref) ?? 'Enter the Credit Committee reference.'
            }}</mat-error>
          </mat-form-field>
        }

        @if (reinstating()) {
          <fieldset class="modal-choice">
            <legend>All of these must hold</legend>
            <mat-checkbox formControlName="trigger_resolved" data-testid="ins-resolved">
              The original suspension trigger is resolved
            </mat-checkbox>
            <mat-checkbox formControlName="no_new_adverse_events" data-testid="ins-no-adverse">
              No new adverse events were recorded during the suspension
            </mat-checkbox>
            @if (
              fieldError(form.controls.trigger_resolved) ??
              fieldError(form.controls.no_new_adverse_events)
            ) {
              <span class="modal-choice__error" role="alert">{{
                fieldError(form.controls.trigger_resolved) ??
                  fieldError(form.controls.no_new_adverse_events)
              }}</span>
            }
          </fieldset>
        }

        <mat-form-field appearance="outline">
          <mat-label>CGIC reference (optional)</mat-label>
          <input matInput formControlName="cgic_reference" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Reason</mat-label>
          <textarea matInput rows="3" formControlName="reason" data-testid="ins-reason"></textarea>
          <mat-error>{{
            fieldError(form.controls.reason) ?? 'Give a reason of at least 20 characters.'
          }}</mat-error>
        </mat-form-field>
      </div>
    </app-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceStatusDialogComponent {
  protected readonly data = inject<InsuranceStatusDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<InsuranceStatusDialogComponent, boolean>>(MatDialogRef);

  protected readonly moves = movesFrom(this.data.current);
  protected readonly fieldError = fieldError;

  protected readonly form = inject(FormBuilder).nonNullable.group({
    to_state: [this.moves[0]?.to ?? ('ACTIVE' as InsuranceState)],
    trigger_id: [''],
    cc_ref: [''],
    trigger_resolved: [false],
    no_new_adverse_events: [false],
    cgic_reference: [''],
    reason: [''],
  });
  private readonly target = signal<InsuranceState>(this.form.controls.to_state.value);
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);

  constructor() {
    this.form.controls.to_state.valueChanges.pipe(takeUntilDestroyed()).subscribe((to) => {
      this.target.set(to);
      this.form.controls.trigger_id.setValue('');
    });
  }

  protected triggerOptions(): InsuranceTrigger[] {
    const to = this.target();
    return to === 'SUSPENDED' || to === 'WITHDRAWN' ? this.data.triggers[to] : [];
  }

  protected reinstating(): boolean {
    return this.data.current === 'SUSPENDED' && this.target() === 'ACTIVE';
  }

  protected needsReference(): boolean {
    return this.target() === 'WITHDRAWN' || this.reinstating();
  }

  protected save(): void {
    const v = this.form.getRawValue();
    const to = this.target();
    const trigger = this.triggerOptions().length > 0;
    const missing = flagMissing([
      [this.form.controls.trigger_id, trigger && !v.trigger_id ? 'Name the trigger.' : null],
      [
        this.form.controls.cc_ref,
        this.needsReference() && !v.cc_ref.trim() ? 'Enter the Credit Committee reference.' : null,
      ],
      [
        this.form.controls.trigger_resolved,
        this.reinstating() && !v.trigger_resolved
          ? 'Confirm the original trigger is resolved.'
          : null,
      ],
      [
        this.form.controls.no_new_adverse_events,
        this.reinstating() && !v.no_new_adverse_events
          ? 'Confirm no new adverse events were recorded.'
          : null,
      ],
      [
        this.form.controls.reason,
        v.reason.trim().length < 20 ? 'Give a reason of at least 20 characters.' : null,
      ],
    ]);
    if (missing) return;

    this.pending.set(true);
    this.error.set(null);
    this.data
      .save({
        to_state: to,
        reason: v.reason.trim(),
        trigger_id: v.trigger_id || undefined,
        cc_ref: v.cc_ref.trim() || undefined,
        cgic_reference: v.cgic_reference.trim() || undefined,
        trigger_resolved: this.reinstating() ? v.trigger_resolved : undefined,
        no_new_adverse_events: this.reinstating() ? v.no_new_adverse_events : undefined,
      })
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (err: unknown) => {
          this.pending.set(false);
          this.error.set(placeRefusal(this.form, err, GENERIC_ERROR));
        },
      });
  }
}
